import React, { useCallback, useEffect, useMemo, useState } from 'react';

import debounce from 'lodash/debounce';

import { useTasksState } from '../helpers';
import { TasksContext } from './tasks_context';
import { getDefaults, isFilterEnabled, serializeFilters } from '../filters/helpers';
import { filtersStorageKey, loadStoredFilters, saveStoredFilters } from '../filters/storage';
import { useQueryParamsState } from 'hooks/use_query_param_state';

import type { ReactNode } from 'react';
import type { ForUser, Task } from 'bundles/apex/tasks/types';
import type { Parent } from 'bundles/crm/types';
import type { Filters } from '../filters/types';
import type { Props as TaskProps } from '../task';
import type { TasksContextValue, TasksPaths } from './tasks_context';

const URL_PARAM_TYPES = {
  assigneeId: 'number',
  completed: 'boolean',
  fromDate: 'string',
  officeId: 'number',
  open: 'boolean',
  overdue: 'boolean',
  parentType: 'string',
  query: 'string',
  sortBy: 'string',
  sortOrder: 'string',
  tbd: 'boolean',
  toDate: 'string',
  allTasks: 'boolean',
  type: 'string'
} as const;

const FILTERS_URL_PREFIX = 'tasks';

export interface TasksProviderProps {
  allowParentSelection?: boolean;
  children: ReactNode;
  currentParent?: Parent;
  disableAssignee?: boolean;
  features?: TaskProps['features'];
  filtersConfig: TasksContextValue['filtersConfig'];
  forUser: ForUser;
  hideAfterReparenting?: boolean;
  initialFilters?: Partial<Filters>;
  options?: TasksContextValue['options'];
  paginate?: boolean;
  paths: TasksPaths;
  showParents?: TaskProps['showParents'];
  showRouterPrompt?: boolean;
  taskMenuOptions?: TasksContextValue['taskMenuOptions'];
}

export function TasksProvider({
  allowParentSelection,
  children,
  currentParent,
  disableAssignee,
  features,
  filtersConfig,
  forUser,
  hideAfterReparenting,
  initialFilters,
  options = {},
  paginate = false,
  paths,
  showParents,
  showRouterPrompt = false,
  taskMenuOptions,
}: TasksProviderProps) {
  const state = useTasksState({ paginate, paths });

  const [showModal, setShowModal] = useState(false);

  const showTaskModal = useCallback((task?: Task) => {
    state.setSelectedTask(task || null);
    setShowModal(true);
  }, [state.setSelectedTask]);

  const enabledFilters = filtersConfig as unknown as Record<string, unknown>;
  const users = filtersConfig.options.users;

  const filterDefaults = useMemo<Filters>(() => getDefaults({
    assigneeId: (isFilterEnabled(enabledFilters, 'assignee') && users.find(user => user.id === forUser.id))
      ? forUser.id
      : null,
    overrides: initialFilters,
  }), []);

  const storageKey = useMemo(
    () => filtersStorageKey(forUser.id, currentParent),
    [forUser.id, currentParent?.type, currentParent?.id]
  );

  const [filters, setFiltersState] = useQueryParamsState<Filters>(
    URL_PARAM_TYPES,
    filterDefaults,
    { prefix: FILTERS_URL_PREFIX }
  );

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const hasUrlFilters = Object.keys(URL_PARAM_TYPES).some(name => params.has(`${FILTERS_URL_PREFIX}_${name}`));
    const stored = hasUrlFilters ? null : loadStoredFilters(storageKey);
    const loadedFilters = stored ? { ...filterDefaults, ...stored } : filters;

    if (stored) {
      setFiltersState(loadedFilters);
    }

    state.setFilters(serializeFilters(loadedFilters));
    saveStoredFilters(storageKey, loadedFilters);
  }, []);

  const debouncedDispatch = useMemo(
    () => debounce(state.setFilters, 500),
    [state.setFilters]
  );

  const changeFilters = useCallback((changes: Partial<Filters>, debouncing = false) => {
    const next = { ...filters, ...changes } as Filters;
    setFiltersState(next);
    state.setLoading(true);
    const callback = debouncing ? debouncedDispatch : state.setFilters;
    callback(serializeFilters(next));
    saveStoredFilters(storageKey, next);
  }, [filters, state.setFilters, debouncedDispatch, state.setLoading, setFiltersState, storageKey]);

  const value = useMemo<TasksContextValue>(
    () => ({
      allowParentSelection,
      tasks: state.tasks,
      loading: state.loading,
      pagination: state.pagination,
      selectedTask: state.selectedTask,
      showModal,
      setFilters: state.setFilters,
      setLoading: state.setLoading,
      setPage: state.setPage,
      setSelectedTask: state.setSelectedTask,
      setShowModal,
      showTaskModal,
      reloadTasks: state.reloadTasks,
      createTask: state.createTask,
      updateTask: state.updateTask,
      deleteTask: state.deleteTask,
      addTaskAttachment: state.addTaskAttachment,
      removeTaskAttachment: state.removeTaskAttachment,
      removeTaskFromList: state.removeTaskFromList,
      replaceTask: state.replaceTask,
      changeFilters,
      currentParent,
      disableAssignee,
      features,
      filters,
      filterDefaults,
      filtersConfig,
      forUser,
      hideAfterReparenting,
      options,
      paginate,
      paths,
      showParents,
      showRouterPrompt,
      taskMenuOptions,
    }),
    [
      allowParentSelection,
      state.tasks,
      state.loading,
      state.pagination,
      state.selectedTask,
      state.setFilters,
      state.setLoading,
      state.setPage,
      state.setSelectedTask,
      state.reloadTasks,
      state.createTask,
      state.updateTask,
      state.deleteTask,
      state.addTaskAttachment,
      state.removeTaskAttachment,
      state.removeTaskFromList,
      state.replaceTask,
      changeFilters,
      currentParent,
      disableAssignee,
      features,
      filters,
      filterDefaults,
      filtersConfig,
      forUser,
      hideAfterReparenting,
      options,
      paginate,
      paths,
      showModal,
      showParents,
      showRouterPrompt,
      showTaskModal,
      taskMenuOptions,
    ],
  );

  return (
    <TasksContext.Provider value={value}>
      {children}
    </TasksContext.Provider>
  );
}
